import React from 'react';
import { Images, PermissionList, RolePermission, claimEnums } from 'utils';
import {
    DateRow,
    DateRowStartEnd,
    EditWithIcon,
    LinkRow,
    LocalDateRow,
    MinLoader,
    PriceInput,
    PriceRow,
    renderCptCodeAndModifiers,
    SimpleTooltip,
    TextRow,
    TypeRow,
    UnitsRow,
} from 'components';
import { ClaimAdjustmentCell } from '../../claim/core';
import { OpenClaimDetails } from '../../claim/openClaimDetails';
import { DownloadEob } from './downloadEob';

export const claimPaymentTabsLabels = [
    { label: 'Details' },
    { label: 'Comments' },
    RolePermission([PermissionList.CLAIM_NOTE_MANAGE?.code]) && { label: 'Notes' },
    RolePermission([PermissionList.CLAIM_HISTORY_READ?.code]) && { label: 'History' },
    RolePermission([PermissionList.CLAIM_FILE_READ?.code]) && { label: 'Files' },
];

export function remittanceDetailInfo(claim) {
    return [
        {
            detailText: 'Payment Date:',
            detail: <DateRow date={claim?.paymentDate} />,
        },
        {
            detailText: 'Total Amt:',
            detail: <PriceRow info={claim?.totalAmount} />,
        },
        {
            detailText: 'Reference #:',
            detail: <TextRow name={claim?.paymentReference} />,
        },
        {
            detailText: 'Created Date:',
            detail: <LocalDateRow date={claim?.createdAt} />,
            // detail: <DateRow date={claim?.createdAt} />,
        },
        {
            detailText: 'Applied Amt:',
            detail: <PriceRow info={claim?.appliedAmount} />,
        },
        {
            detailText: 'Bank:',
            detail: <TextRow name={claim?.bankName} />,
        },
        {
            detailText: 'Payer:',
            detail: (
                <LinkRow
                    name={claim?.payerName ? claim?.payerName : '--'}
                    href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${claim?.payerId}` : ''}
                    checkPermission={claim?.payerId ? RolePermission([PermissionList.FS_READ.code]) : null}
                />
            ),
        },
        {
            detailText: 'Adjustment AMT:',
            detail: (
                <div className="space-between">
                    <PriceRow info={claim?.adjustmentAmount} />
                    {(claim?.adjustmentReason || claim?.adjustmentAmount === 0) && (
                        <SimpleTooltip
                            title={
                                <div>
                                    <p>Reason</p>
                                    <p style={{ fontWeight: 600, marginTop: 4 }}>
                                        {claim?.adjustmentReason
                                            ? claim?.adjustmentReason
                                            : claim?.adjustmentAmount === 0
                                              ? 'If this claim requires an adjustment, please enter or edit the adjustment amount.'
                                              : ''}
                                    </p>
                                </div>
                            }
                            placement="bottom"
                            arrow
                        >
                            <img
                                style={{
                                    filter: 'brightness(0) saturate(100%) invert(41%) sepia(18%) saturate(461%) hue-rotate(183deg) brightness(101%) contrast(88%)',
                                }}
                                src={Images.toolInfoIcon}
                                alt="info"
                            />
                        </SimpleTooltip>
                    )}
                </div>
            ),
        },
        {
            detailText: 'Memo:',
            detail: <TextRow name={claim?.memo ? claim?.memo : '--'} />,
        },
        {
            detailText: 'Payment Method:',
            detail: <TypeRow text={claim?.paymentMethod} />,
        },
        {
            detailText: 'Open Amt:',
            detail: <PriceRow info={claim?.openAmount} />,
        },
        claim?.eob?.file && {
            detailText: 'EOB:',
            detail: <DownloadEob file={claim?.eob?.file} />,
        },
    ];
}

export const linkedClaimTableInfo = (
    classes,
    handleAction,
    handleChange,
    currentInput,
    activeRow,
    inputs,
    loadSave,
    checkError,
    remittanceById,
    getRemittanceClaims,
) => {
    const style = {
        height: '36px',
        borderBottom: `1px solid ${activeRow ? '#347AF0' : '#D8E5FF'}`,
    };

    const styleNoBorder = {
        height: '36px',
        borderBottom: `1px solid #FE7070`,
    };

    return {
        loadingType: 'GET_REMITTANCE_BY_ID_CLAIMS',

        head: [
            { name: 'displayId', title: 'Claim ID', noSearch: true, width: '8%' },
            { name: 'dateRange', title: 'DOS', iconType: 'date', noSearch: true, width: '14%' },
            { name: 'client', title: 'Client', noSearch: true, width: '15%' },
            { name: 'billedAmt', title: 'Total Billed', noSearch: true, iconType: 'date', width: '10%' },
            { name: 'billedAmt', title: 'Total Allowed', noSearch: true, iconType: 'date', width: '10%' },
            { name: 'paidAmt', title: 'Total Paid', noSearch: true, iconType: 'date', width: '10%' },
            { name: 'balance', title: 'Balance', noSearch: true, custom: false, width: '10%' },
            { name: 'aging', title: 'Aging', noSearch: true, iconType: 'date', width: '10%' },
            { name: 'action', title: ' ', noSearch: true, custom: false, width: '15%' },
        ],

        body: (item) => {
            return [
                {
                    value: (
                        <OpenClaimDetails claimId={item?.id} displayId={item?.displayId} afterSuccess={getRemittanceClaims} />
                        // <LinkRow
                        //     name={item?.displayId ? item?.displayId : '--'}
                        //     href={RolePermission([PermissionList.CLAIMS_READ?.code]) ? `/billedClaim/${item?.id}` : ''}
                        //     checkPermission={item?.displayId ? RolePermission([PermissionList.CLAIMS_READ?.code]) : null}
                        // />
                    ),
                    width: '8%',
                },
                { value: <DateRowStartEnd start={item?.dos?.early} end={item?.dos?.latest} />, width: '14%' },
                {
                    value: (
                        <LinkRow
                            name={item?.clientFullName ? item.clientFullName : '--'}
                            href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.clientId}` : ''}
                            checkPermission={item?.clientId ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                        />
                    ),
                    notClickable: true,
                    width: '15%',
                },

                { value: <PriceRow info={item?.billedAmount ? item?.billedAmount : 0} />, width: '10%' },
                { value: <PriceRow info={item?.allowedAmount ? item?.allowedAmount : 0} />, width: '10%' },
                { value: <PriceRow info={item?.paidAmount ? item?.paidAmount : 0} />, width: '10%' },
                { value: <PriceRow info={item?.remaining ? item?.remaining : 0} />, width: '10%' },
                {
                    value: (
                        <div
                            style={{
                                color: item?.aging
                                    ? item?.status === claimEnums?.PAID
                                        ? 'rgb(2, 122, 72)'
                                        : item?.status === claimEnums?.OPEN
                                        ? '#FFCA33'
                                        : ''
                                    : '',
                                fontWeight: 600,
                            }}>
                            {item?.aging ? item?.aging : '--'}
                        </div>
                    ),
                    width: '10%',
                },
                {
                    value: remittanceById?.status !== 'VOID' && (
                        <div className={classes.claimPaymentBodyHeadItemButtons}>
                            {activeRow === item?.id ? (
                                <div className="flex-align-center">
                                    <button onClick={() => handleAction(item, 'save')} className="save-btn-box">
                                        {loadSave?.length ? <MinLoader margin={'0'} color={'#344054'} position="relative" /> : 'Save'}
                                    </button>
                                    <button onClick={() => handleAction(item, 'close')} className="posting-close-btn">
                                        <img src={Images.remove} alt="icon" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex-align-center" style={{ gap: 8 }}>
                                    <EditWithIcon
                                        id={'edit-form'}
                                        icon={Images.edit}
                                        type={'button'}
                                        handleClick={() => {
                                            handleAction(item, 'edit');
                                        }}
                                    />
                                    <EditWithIcon
                                        id={'delete-form'}
                                        icon={Images.remove}
                                        type={'button'}
                                        handleClick={() => {
                                            handleAction(item, 'remove');
                                        }}
                                    />

                                    {/*{(item?.status === claimEnums.OPEN || item?.status === claimEnums.APPEAL) && (*/}
                                    {/*    <SimpleTooltip*/}
                                    {/*        title={*/}
                                    {/*            !!item?.secondaryBilling?.secondaryBillingState*/}
                                    {/*                ? 'Unmark for Secondary Billing'*/}
                                    {/*                : 'Mark for Secondary Billing'*/}
                                    {/*        }*/}
                                    {/*        placement="top"*/}
                                    {/*        arrow*/}
                                    {/*    >*/}
                                    {/*        <div>*/}
                                    {/*            <EditWithIcon*/}
                                    {/*                id={'secondary-billing'}*/}
                                    {/*                icon={renderSecondaryBillingImage(item?.secondaryBilling)}*/}
                                    {/*                type={'button'}*/}
                                    {/*                handleClick={() => {*/}
                                    {/*                    handleAction(item, 'secondaryBilling');*/}
                                    {/*                }}*/}
                                    {/*            />*/}
                                    {/*        </div>*/}
                                    {/*    </SimpleTooltip>*/}
                                    {/*)}*/}
                                </div>
                            )}
                        </div>
                    ),
                    notClickable: true,
                    width: '15%',
                },
            ];
        },

        renderAccordionRow: (i) => i?.bills,

        accordionHead: [
            { title: 'Bill ID', width: '8%' },
            { title: 'DOS', width: '8%' },
            { title: 'CPT Code (Mod.)', width: '10%' },
            { title: 'Units', width: '5%' },
            { title: 'Billed ', width: '8%' },
            { title: 'Allowed ', width: '8%' },
            { title: 'Deduct.', width: '8%' },
            { title: 'Copay', width: '8%' },
            { title: 'CoINS', width: '8%' },
            { title: 'Paid Amt ', width: '8%' },
            { title: 'Prev. Paid ', width: '8%' },
            { title: 'Adjustments', width: '8%' },
            { title: 'Balance ', width: '8%' },
        ],

        accordionBody: (i, j, parent) => {
            return [
                { value: <TextRow name={i?.displayId ? i?.displayId : '--'} />, width: '8%' },
                { value: <DateRow date={i?.dos} />, width: '8%' },
                {
                    value: <TextRow name={renderCptCodeAndModifiers(i?.cptCode, i?.chargeRateModifier, i?.chargeRateModifier2, i?.chargeRateModifier3, i?.posModifier)} />,
                    width: '10%',
                },
                { value: <UnitsRow info={i?.totalUnits} />, width: '5%' },
                { value: <PriceRow info={i?.totalBilled} />, width: '8%' },

                {
                    value: (
                        <div style={{ ...style }} className={classes.bodyItemBodyRow}>
                            <PriceInput
                                ref={currentInput}
                                placeholder={'$0.00'}
                                name={'allowedAMT'}
                                handleChange={(e) => handleChange(e, j, i)}
                                value={
                                    activeRow
                                        ? inputs[j]?.allowedAMT === 0
                                            ? 0
                                            : inputs[j]?.allowedAMT
                                        : i?.allowedAMT
                                        ? i?.allowedAMT
                                        : 0
                                }
                                disabled={!activeRow}
                                decimalScale={2}
                            />
                        </div>
                    ),
                    width: '8%',
                    padding: '0',
                    notClickable: true,
                },
                {
                    value: (
                        <div style={checkError ? { ...styleNoBorder } : { ...style }} className={classes.bodyItemBodyRow}>
                            <PriceInput
                                ref={currentInput}
                                placeholder={'$0.00'}
                                name={'deductible'}
                                handleChange={(e) => handleChange(e, j, i)}
                                value={
                                    activeRow
                                        ? inputs[j]?.deductible === 0
                                            ? 0
                                            : inputs[j]?.deductible
                                        : i?.deductible
                                        ? i?.deductible
                                        : 0
                                }
                                disabled={!activeRow}
                                decimalScale={2}
                            />
                        </div>
                    ),
                    width: '8%',
                    padding: '0',
                    notClickable: true,
                },
                {
                    value: (
                        <div style={checkError ? { ...styleNoBorder } : { ...style }} className={classes.bodyItemBodyRow}>
                            <PriceInput
                                ref={currentInput}
                                placeholder={'$0.00'}
                                name={'copay'}
                                handleChange={(e) => handleChange(e, j, i)}
                                value={activeRow ? (inputs[j]?.copay === 0 ? 0 : inputs[j]?.copay) : i?.copay ? i?.copay : 0}
                                disabled={!activeRow}
                                decimalScale={2}
                            />
                        </div>
                    ),
                    width: '8%',
                    padding: '0',
                    notClickable: true,
                },
                {
                    value: (
                        <div style={checkError ? { ...styleNoBorder } : { ...style }} className={classes.bodyItemBodyRow}>
                            <PriceInput
                                ref={currentInput}
                                placeholder={'$0.00'}
                                name={'coIns'}
                                handleChange={(e) => handleChange(e, j, i)}
                                value={activeRow ? (inputs[j]?.coIns === 0 ? 0 : inputs[j]?.coIns) : i?.coIns ? i?.coIns : 0}
                                disabled={!activeRow}
                                decimalScale={2}
                            />
                        </div>
                    ),
                    width: '8%',
                    padding: '0',
                    notClickable: true,
                },
                {
                    value: (
                        <div style={checkError ? { ...styleNoBorder } : { ...style }} className={classes.bodyItemBodyRow}>
                            <PriceInput
                                ref={currentInput}
                                placeholder={'$0.00'}
                                name={'paidAMT'}
                                handleChange={(e) => handleChange(e, j, i)}
                                value={
                                    activeRow ? (inputs[j]?.paidAMT === 0 ? 0 : inputs[j]?.paidAMT) : i?.paidByThis ? i?.paidByThis : 0
                                    // : inputs[j]?.paidAMT : i?.payorPaid ? i?.payorPaid : 0
                                }
                                disabled={!activeRow}
                                decimalScale={2}
                            />
                        </div>
                    ),
                    width: '8%',
                    padding: '0',
                    notClickable: true,
                },
                { value: <PriceRow info={i?.payorPaid} />, width: '8%' },
                {
                    value: <ClaimAdjustmentCell claim={parent} item={i} afterSuccess={() => getRemittanceClaims('noLoad')} />,
                    notClickable: true,
                    width: '8%',
                },
                { value: <PriceRow info={i?.payorBalance} />, width: '8%' },
            ];
        },

        noDetailsText: 'No bills yet',
        notYet: {
            title: 'No Billable Appts',
            // subTitle: 'There are no billable appts.',
            image: Images.noPendingClaim,
            height: '180px!important',
        },
    };
};
